import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Space, Button, Select, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined, PushpinFilled } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnProbeService from '../../services/cdnProbeService';

const { Text } = Typography;
const fmtTime = (s) => (s ? new Date(s.replace(' ', 'T')).toLocaleString() : '-');
const num = (v) => (v === null || v === undefined ? '-' : Math.round(v));
const pct = (v) => (v === null || v === undefined ? '-' : `${Math.round(v * 100)}%`);

/** 回源选路(按分组):机房 × 源站分组的决策,可手动固定 */
const OriginGroupRoutes = () => {
    const { t } = useTranslation();
    const [data, setData] = useState({ mode: '0', rows: [], accel: {}, cidr_count: 0 });
    const [loading, setLoading] = useState(false);
    const [colo, setColo] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await cdnProbeService.getOriginGroupRoutes();
        setLoading(false);
        if (r.success) setData(r.data || { mode: '0', rows: [], accel: {}, cidr_count: 0 });
        else message.error(r.error || t('Failed to load'));
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const routeName = useCallback((via) => {
        if (via === null || via === undefined) return '-';
        return via === 'direct' ? t('Direct') : (data.accel[via] || via);
    }, [data.accel, t]);

    const colos = useMemo(() => [...new Set(data.rows.map((r) => r.colo))], [data.rows]);
    const rows = useMemo(() => data.rows.filter((r) => !colo || r.colo === colo), [data.rows, colo]);

    const pin = async (row, via) => {
        const r = await cdnProbeService.pinOriginGroupRoute(row.colo, row.group_id, via);
        if (r.success) { message.success(t('Saved')); load(); }
        else message.error(r.error || t('Save failed'));
    };

    const candTip = (id, c) => (id === 'direct'
        ? `RTT ${num(c.rtt)} ms · ${t('Cold connections')} ${pct(c.cold)}`
        : `${t('To accelerator')} ${num(c.rtt_leg)} ms (${pct(c.cold_leg)}) + ${t('To origin')} ${num(c.rtt_origin)} ms (${pct(c.cold_origin)})`);

    const columns = [
        { title: t('Colo'), dataIndex: 'colo', width: 70 },
        {
            title: t('Origin group'), width: 230,
            render: (_, r) => (
                <Tooltip title={(r.prefixes || []).join(', ')}>
                    <Space size={4} wrap>
                        {r.asn && <Text type="secondary">AS{r.asn}</Text>}
                        {(r.cidrs || []).map((c) => <Text key={c} code>{c}</Text>)}
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: t('Route'), width: 190,
            render: (_, r) => {
                const via = r.pinned ?? r.choice;
                return (
                    <Space size={4}>
                        <Tag color={via === null ? 'default' : (via === 'direct' ? 'blue' : 'purple')}>{via === null ? t('Default') : routeName(via)}</Tag>
                        {r.pinned && <Tooltip title={t('Pinned')}><PushpinFilled style={{ color: '#fa8c16' }} /></Tooltip>}
                    </Space>
                );
            },
        },
        {
            title: t('Estimated (ms)'),
            render: (_, r) => {
                const cs = Object.entries(r.candidates || {});
                const best = cs.filter(([, c]) => c.ok && c.ms != null).sort((a, b) => a[1].ms - b[1].ms)[0]?.[0];
                return (
                    <Space size={[4, 4]} wrap>
                        {cs.map(([id, c]) => (
                            <Tooltip key={id} title={candTip(id, c)}>
                                <Tag color={!c.ok ? 'default' : (id === best ? 'green' : undefined)} style={!c.ok ? { textDecoration: 'line-through' } : undefined}>
                                    {routeName(id)} {c.ms == null ? '-' : Math.round(c.ms)}
                                </Tag>
                            </Tooltip>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: t('Last switch'), dataIndex: 'switched_at', width: 170,
            render: (s, r) => (
                <Tooltip title={(r.history || []).slice(0, 8).map((h) => `${fmtTime(h.at)} ${routeName(h.from)} → ${routeName(h.to)}`).join('\n')}
                    overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 420 }}>
                    <Text type="secondary">{fmtTime(s)}</Text>
                </Tooltip>
            ),
        },
        {
            title: t('Pin'), width: 200,
            render: (_, r) => (
                <Select size="small" style={{ width: 180 }} value={r.pinned ?? ''} onChange={(v) => pin(r, v)}
                    options={[
                        { value: '', label: t('Auto') },
                        { value: 'direct', label: t('Direct') },
                        ...Object.keys(r.candidates || {}).filter((id) => id !== 'direct').map((id) => ({ value: id, label: routeName(id) })),
                    ]} />
            ),
        },
    ];

    return (
        <Card
            title={(
                <Space>
                    {t('By origin group')}
                    {data.mode === 'observe' ? <Tag color="gold">{t('Observe only')}</Tag> : (data.mode !== '1' && <Tag>{t('Off')}</Tag>)}
                    <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>{t('Delivered prefixes')}: {data.cidr_count}</Text>
                </Space>
            )}
            extra={(
                <Space>
                    <Select allowClear size="small" style={{ width: 120 }} placeholder={t('All colos')} value={colo} onChange={setColo}
                        options={colos.map((c) => ({ value: c, label: c }))} />
                    <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
                </Space>
            )}
        >
            <Table rowKey={(r) => `${r.colo}|${r.group_id}`} size="small" loading={loading} columns={columns} dataSource={rows}
                pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1100 }} />
        </Card>
    );
};

export default OriginGroupRoutes;
